// FILE: src/components/AdminMetrics.jsx
import React, { useEffect, useState } from "react";
import SuperAdminNav from "./SuperAdminNav";
import { auth } from "../firebase";

const B = {
  red: "#980000",
  redDark: "#650202",
  orange: "#f15822",
  navy: "#003353",
  steel: "#657899",
  bg: "#f6f7fb",
  white: "#ffffff",
  border: "#e8ebf3",
  green: "#065f46",
  greenBg: "#ecfdf5",
};

function StatCard({ label, value, sub, color, icon }) {
  return (
    <div style={{
      background: B.white, border: `1px solid ${B.border}`, borderRadius: 14,
      padding: "20px 20px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 13, color: B.steel, fontWeight: 600 }}>{label}</div>
        {icon && <span style={{ fontSize: 20 }}>{icon}</span>}
      </div>
      <div style={{ fontSize: 32, fontWeight: 900, color: color || B.redDark, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: B.steel, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h2 style={{ margin: "0 0 16px", color: B.redDark, fontSize: 18, fontWeight: 800 }}>{title}</h2>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 14,
      }}>
        {children}
      </div>
    </div>
  );
}

function PlanRow({ plan, count }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 0", borderBottom: `1px solid ${B.border}`,
    }}>
      <span style={{ fontSize: 14, color: B.redDark, fontWeight: 600, textTransform: "capitalize" }}>{plan}</span>
      <span style={{
        background: "#fff0ee", color: B.red, borderRadius: 999,
        padding: "3px 10px", fontSize: 13, fontWeight: 700,
        border: `1px solid ${B.orange}`,
      }}>{count}</span>
    </div>
  );
}

export default function AdminMetrics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const cu = auth.currentUser;
        const token = cu ? await cu.getIdToken() : null;
        const res = await fetch("/api/admin/metrics", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || "Failed to load metrics");
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div style={{ background: B.bg, minHeight: "100vh", padding: "24px 20px 60px", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <SuperAdminNav title="Metrics" active="metrics" />

        {loading && (
          <div style={{ textAlign: "center", padding: 60, color: B.steel, fontSize: 15 }}>
            Loading metrics...
          </div>
        )}

        {error && (
          <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 12, padding: "14px 18px", color: "#7f1d1d", fontSize: 14 }}>
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Last updated */}
            <div style={{ fontSize: 12, color: B.steel, marginBottom: 24 }}>
              Last updated: {new Date(data.generatedAt).toLocaleString()}
            </div>

            {/* Users */}
            <Section title="👥 Users">
              <StatCard label="Total users" value={data.users.total} icon="👤" />
              <StatCard label="Personal accounts" value={data.users.personal} icon="🙋" />
              <StatCard label="Business accounts" value={data.users.business} icon="🏢" />
              <StatCard label="Verified emails" value={data.users.verified} color={B.green} icon="✅" sub={`${data.users.unverified} unverified`} />
              <StatCard label="Active (30 days)" value={data.users.activeLast30Days} icon="🟢" />
              <StatCard label="New this month" value={data.users.newThisMonth} icon="📅" />
              <StatCard label="New this week" value={data.users.newThisWeek} icon="📆" />
            </Section>

            {/* Subscriptions */}
            <Section title="💳 Subscriptions">
              <StatCard label="Active personal" value={data.subscriptions.activePersonal} color={B.green} icon="✅" />
              <StatCard label="Past due" value={data.subscriptions.pastDue} color="#b91c1c" icon="⚠️" />
              <StatCard label="Cancelled" value={data.subscriptions.cancelled} color={B.steel} icon="❌" />
            </Section>

            {/* Plan breakdown */}
            {Object.keys(data.subscriptions.planBreakdown).length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <h2 style={{ margin: "0 0 16px", color: B.redDark, fontSize: 18, fontWeight: 800 }}>📊 Plan breakdown</h2>
                <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 14, padding: "4px 20px 8px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", maxWidth: 400 }}>
                  {Object.entries(data.subscriptions.planBreakdown).map(([plan, count]) => (
                    <PlanRow key={plan} plan={plan} count={count} />
                  ))}
                </div>
              </div>
            )}

            {/* Tenants */}
            <Section title="🏢 Business tenants">
              <StatCard label="Total tenants" value={data.tenants.total} icon="🏢" />
              <StatCard label="Active tenants" value={data.tenants.active} color={B.green} icon="✅" />
              <StatCard label="Starter plan" value={data.tenants.starter} icon="🌱" />
              <StatCard label="Growth plan" value={data.tenants.growth} icon="🚀" />
              <StatCard label="Total members" value={data.tenants.totalMembers} icon="👥" />
            </Section>

            {/* Revenue */}
            <Section title="💰 Revenue (estimated MRR)">
              <StatCard label="Personal MRR" value={`€${data.revenue.personalMRR.toFixed(2)}`} color={B.green} icon="💳" sub="Based on active personal plans" />
              <StatCard label="Business MRR" value={`€${data.revenue.businessMRR.toFixed(2)}`} color={B.navy} icon="🏢" sub="Based on active business plans" />
              <StatCard label="Total MRR" value={`€${data.revenue.totalMRR.toFixed(2)}`} color={B.redDark} icon="💰" sub="Combined estimated MRR" />
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
